export const isDescHtmlEmpty = (html: string | undefined | null): boolean => {
  if (!html) return true;
  if (html === "<p></p>") return true;
  if (html?.includes("<img")) return false;
  const textOnly = html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/g, " ")
    .replace(/\u00A0/g, " ")
    .trim();
  return textOnly === "";
};

export function fieldErrorPresent(v: unknown): boolean {
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v?.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return true;
}

export function scrollIntoFormContainer(
  container: HTMLElement,
  target: HTMLElement,
  marginTop = 20
) {
  const cRect = container.getBoundingClientRect();
  const eRect = target.getBoundingClientRect();
  const nextTop = container.scrollTop + (eRect.top - cRect.top) - marginTop;
  container.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
}

function findByInputId(container: HTMLElement, inputId: string) {
  const el = container?.querySelector(`#${CSS.escape(inputId)}`);
  return (el as HTMLElement | null)?.closest(
    ".flex.flex-col"
  ) as HTMLElement | null;
}

function findByDropdownId(container: HTMLElement, dropdownId: string) {
  const el = container?.querySelector(`#${CSS.escape(dropdownId)}-trigger`);
  return (el as HTMLElement | null)?.closest(
    ".flex.flex-col"
  ) as HTMLElement | null;
}

function findByDataAttr(container: HTMLElement, attr: string) {
  return container?.querySelector(
    `[data-form-field="${attr}"]`
  ) as HTMLElement | null;
}

const FORM_FIELD_ORDER: {
  key: string;
  find: (c: HTMLElement) => HTMLElement | null;
}[] = [
  { key: "ticketCategory", find: (c) => findByDataAttr(c, "ticketCategory") },
  { key: "fullName", find: (c) => findByInputId(c, "fullName") },
  { key: "email", find: (c) => findByInputId(c, "email") },
  { key: "siteName", find: (c) => findByDropdownId(c, "site-name-dropdown") },
  { key: "ticketSubject", find: (c) => findByInputId(c, "ticketSubject") },
  {
    key: "systemName",
    find: (c) => findByDropdownId(c, "system-name-dropdown"),
  },
  {
    key: "issueType",
    find: (c) => findByDropdownId(c, "issue-type-dropdown"),
  },
  { key: "priority", find: (c) => findByDataAttr(c, "priority") },
  {
    key: "ticketDescription",
    find: (c) => findByDataAttr(c, "ticketDescription"),
  },
];

export function findFirstCreateTicketErrorTarget(
  container: HTMLElement,
  errors: Record<string, unknown>,
  orderedCustomFieldKeys: string[]
): HTMLElement | null {
  for (const { key, find } of FORM_FIELD_ORDER) {
    if (fieldErrorPresent(errors[key])) {
      const el = find(container);
      if (el) return el;
    }
  }

  const cf = errors.custom_fields as Record<string, unknown> | undefined;
  if (cf && typeof cf === "object") {
    for (const key of orderedCustomFieldKeys) {
      if (fieldErrorPresent(cf[key])) {
        const el = container?.querySelector(
          `[data-cf-field-key="${CSS.escape(key)}"]`
        );
        if (el) return el as HTMLElement;
      }
    }
  }

  return null;
}

export function fallbackScrollToFirstValidationMessage(
  container: HTMLElement
) {
  const messages = container?.querySelectorAll(
    '[role="alert"], span.text-xs.text-verba-red-alert'
  );
  if (messages?.length === 0) return;
  const containerRect = container.getBoundingClientRect();
  let best: Element | null = null;
  let bestOffset = Infinity;
  messages?.forEach((el) => {
    const rect = el.getBoundingClientRect();
    const offset = rect.top - containerRect.top + container.scrollTop;
    if (offset < bestOffset) {
      bestOffset = offset;
      best = el;
    }
  });
  if (best) {
    const row = (best as HTMLElement).closest(".flex.flex-col");
    scrollIntoFormContainer(container, (row ?? best) as HTMLElement);
  }
}
